import type {
  ApiDashboardElement,
  WidgetEntity,
} from '@highimpact-frontend/api-types';
import { WidgetSourceSelector } from '@highimpact/source-selector';
import { useMemo } from 'react';

import { useDashboardScope } from '../../../../context/DashboardHostContext';
import { setElementEntityScopeFromWidgetEntity } from '../../../../state/dashboard-element-operations';
import { buildWidgetEntitySelectionFromElement } from './entitySelectionFromCatalog';
import { PlotConfigSection } from './plotConfigFormLayout';
import { useWidgetSourceSelection } from './useWidgetSourceSelection';
import type { EntityScopeWidget } from './widgetDimensionSlots';

export interface EntityScopeSelectorProps {
  readonly widget: EntityScopeWidget;
  readonly config: ApiDashboardElement;
  readonly onChange: (config: ApiDashboardElement) => void;
  readonly title?: string;
}

/**
 * Shared v2 entity selector for widgets scoped to a project-site pair.
 */
export function EntityScopeSelector({
  widget,
  config,
  onChange,
  title = 'Entité',
}: EntityScopeSelectorProps) {
  const scope = useDashboardScope();
  const { loading, error, data } = useWidgetSourceSelection({ widget, scope });

  const initialEntitySelection = useMemo(
    () => buildWidgetEntitySelectionFromElement(config, data),
    [config, data]
  );

  const handleEntitySelect = (selectedEntities: WidgetEntity[]) => {
    const selectedEntity = selectedEntities[0] ?? null;
    const nextConfig = setElementEntityScopeFromWidgetEntity(
      selectedEntity,
      config
    );
    onChange(nextConfig);
  };

  return (
    <PlotConfigSection title={title}>
      <WidgetSourceSelector
        data={data}
        loading={loading}
        error={error}
        initialEntitySelection={initialEntitySelection}
        onEntitySelect={handleEntitySelect}
        entitiesMultiple={false}
        matchControlPanelStyle
      />
    </PlotConfigSection>
  );
}
